// เรียก API ของ cpac-livestream-service (โหมด direct)

import moment from 'moment'
import { API_TIMEOUT_MS } from './constants'

const BASE = (process.env.REACT_APP_LIVESTREAM_API_ENDPOINT || '').replace(
  /\/+$/,
  '',
)

// false = Index / Toolbar ไม่ให้เข้าโหมด direct เลย ทุกเส้นข้างล่างจึงไม่ถูกเรียก
export const API_READY = BASE !== ''

// ไม่ใช้ `class ... extends Error` เพราะ babel ของ react-scripts 2 transpile เป็น ES5
// แล้ว instanceof กับ built-in ที่ถูก extend เชื่อถือไม่ได้ — ตรวจด้วย isApiError() แทน
export function apiError(code, message, extra) {
  const err = new Error(message || code)
  err.name = 'LivestreamApiError'
  err.code = code
  err.isLivestreamApiError = true
  Object.assign(err, extra || {})
  return err
}

export function isApiError(err) {
  return !!(err && err.isLivestreamApiError)
}

export const noop = () => {}

/** code สำรองเมื่อ backend ตอบ error โดยไม่มี body (เช่น ingress ตอบเอง) */
function codeFromStatus(status) {
  if (status === 401) return 'unauthorized'
  if (status === 403) return 'no_permission'
  if (status === 404) return 'not_found'
  if (status === 410) return 'session_ended'
  if (status === 429) return 'rate_limited'
  if (status >= 500) return 'server_error'
  return 'http_error'
}

function problemToError(status, body) {
  const p = body || {}
  return apiError(p.code || codeFromStatus(status), p.detail || p.title, {
    status,
    requestId: p.request_id || null,
    retryable: typeof p.retryable === 'boolean' ? p.retryable : null,
    retryAfterSec: p.retry_after_sec || null,
    endReason: p.end_reason || null,
  })
}

function authHeaders() {
  const token = localStorage.getItem('access-token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

function request(method, path, body) {
  const controller =
    typeof AbortController !== 'undefined' ? new AbortController() : null
  const timer = setTimeout(
    () => controller && controller.abort(),
    API_TIMEOUT_MS,
  )

  const init = {
    method,
    headers: authHeaders(),
    signal: controller ? controller.signal : undefined,
  }
  if (body !== undefined) {
    init.headers['Content-Type'] = 'application/json'
    init.body = JSON.stringify(body)
  }

  return fetch(`${BASE}${path}`, init)
    .then(res => {
      if (res.status === 204) return null
      return res
        .json()
        .catch(() => ({}))
        .then(data => {
          if (!res.ok) throw problemToError(res.status, data)
          return data
        })
    })
    .catch(err => {
      if (isApiError(err)) throw err
      const aborted = err && err.name === 'AbortError'
      throw apiError(aborted ? 'timeout' : 'network_error', err && err.message)
    })
    .finally(() => clearTimeout(timer))
}

const enc = encodeURIComponent

export function getCameras(vehicleId) {
  return request('GET', `/vehicles/${enc(vehicleId)}/cameras`)
}

/** ตอบ ready ทันทีถ้ามีคนดู stream นี้อยู่แล้ว */
export function createSession({ vehicleId, camera, quality }) {
  const payload = { vehicle_id: vehicleId, camera }
  if (quality) payload.quality = quality
  return request('POST', '/sessions', payload)
}

export function getSession(sessionId) {
  return request('GET', `/sessions/${enc(sessionId)}`)
}

/** response อาจมี stream ใหม่ถ้า token หมุน */
export function heartbeat(sessionId) {
  return request('POST', `/sessions/${enc(sessionId)}/heartbeat`)
}

// ตอน logout token ถูกล้างก่อนกรอบถูกถอด → DELETE ได้ 401 แล้ว session ค้าง กล้องส่งภาพต่อ
// จึงใช้ beacon ที่ไม่ต้องใช้ token แทน (ยังไปถึงแม้หน้ากำลัง redirect ไป login)
export function stopSession(sessionId) {
  if (!localStorage.getItem('access-token') && stopSessionOnUnload(sessionId)) {
    return Promise.resolve(null)
  }
  return request('DELETE', `/sessions/${enc(sessionId)}`).catch(err => {
    // token หมดอายุแต่ยังไม่ถูกล้าง → 401 → ลอง beacon
    if (err && err.status === 401 && stopSessionOnUnload(sessionId)) return null
    throw err
  })
}

// ปิดแท็บแล้ว fetch ไปไม่ถึง ต้องใช้ sendBeacon ซึ่งตั้ง header ไม่ได้ จึงไม่มี Authorization
// — backend ถือว่าใครมี session_id คือเจ้าของ
export function stopSessionOnUnload(sessionId) {
  if (!sessionId || !navigator.sendBeacon) return false
  return navigator.sendBeacon(`${BASE}/sessions/${enc(sessionId)}/stop`)
}

// รายงาน QoS แบบ fire-and-forget ล้มเหลวก็ไม่กระทบการเล่น
// event: first_frame | no_first_frame | stalled | recovered | player_error | url_expired
export function sendEvent(sessionId, event, detail) {
  if (!sessionId) return Promise.resolve()
  return request('POST', `/sessions/${enc(sessionId)}/events`, {
    event,
    at: moment().format(),
    detail: detail || {},
  }).catch(noop)
}
